"use client"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Shield, AlertCircle, Send, Lock, Eye, Mail, Info, CheckCircle2, RefreshCcw } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

export function ReportVulnerabilityContent() {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        title: "",
        severity: "medium",
        description: "",
        steps: ""
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            const response = await fetch("/api/report-vulnerability", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    ...formData,
                    to: "security@tokenkickstarter.com"
                })
            })

            if (!response.ok) {
                throw new Error("Failed to submit report")
            }

            setSubmitted(true)
            toast.success("Vulnerability report submitted securely")
        } catch (error) {
            toast.error("Failed to submit report. Please try again or email security@tokenkickstarter.com directly.")
        } finally {
            setIsSubmitting(false)
        }
    }

    if (submitted) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center p-6 pt-28">
                    <Card className="max-w-md w-full text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
                        <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle2 className="h-10 w-10 text-primary" />
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-2xl font-bold">Report Received</h2>
                            <p className="text-muted-foreground">
                                Thank you for your responsible disclosure. Our security team will review your report and get back to you within 24-48 hours.
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                                A confirmation has been sent to <span className="text-primary">security@tokenkickstarter.com</span>
                            </p>
                        </div>
                        <Button variant="outline" onClick={() => window.location.href = "/"} className="w-full">
                            Return Home
                        </Button>
                    </Card>
                </main>
                <Footer />
            </div>
        )
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 pt-24 pb-12 md:pt-28 md:pb-20 lg:pb-24 px-4">
                <div className="container max-w-4xl px-4">
                    {/* Page Header */}
                    <div className="text-center space-y-4 mb-12">
                        <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-2">
                            <Shield className="h-3 w-3 text-primary" />
                            Security First
                        </Badge>
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Report a Vulnerability</h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Help us protect our users and the ecosystem. We reward responsible disclosures of security flaws in our smart contracts and infrastructure.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Guidelines Sidebar */}
                        <div className="lg:col-span-1 space-y-6">
                            <Card className="bg-muted/30 border-none">
                                <CardHeader className="pb-2">
                                    <div className="flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider">
                                        <Info className="h-4 w-4" />
                                        Guidelines
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4 text-sm leading-relaxed">
                                    <p>Please follow these steps for responsible disclosure:</p>
                                    <ul className="list-disc pl-4 space-y-2 text-muted-foreground">
                                        <li>Provide a detailed description of the vulnerability.</li>
                                        <li>Include steps to reproduce or a Proof of Concept (PoC).</li>
                                        <li>Do not disclose the vulnerability publicly until it is resolved.</li>
                                        <li>Avoid any testing that could disrupt service for users.</li>
                                    </ul>
                                </CardContent>
                            </Card>

                            <Card className="bg-primary/5 border-primary/10">
                                <CardHeader className="pb-2">
                                    <div className="flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-wider">
                                        <Lock className="h-4 w-4" />
                                        Secure Channel
                                    </div>
                                </CardHeader>
                                <CardContent className="text-sm text-muted-foreground">
                                    Your submission is encrypted and accessible only by our lead security engineers. For critical concerns, you can also reach us at <span className="text-primary font-semibold">security@tokenkickstarter.com</span>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Report Form */}
                        <div className="lg:col-span-2">
                            <Card className="shadow-2xl shadow-primary/5 border-primary/10">
                                <CardHeader>
                                    <CardTitle>Security Disclosure Form</CardTitle>
                                    <CardDescription>Fill out the details below to submit your report.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Your Name / Handle</label>
                                                <Input
                                                    placeholder="Researcher"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Contact Email</label>
                                                <Input
                                                    type="email"
                                                    placeholder="email@example.com"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Vulnerability Title</label>
                                            <Input
                                                placeholder="Brief descriptive title"
                                                value={formData.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Severity Level</label>
                                            <Select
                                                value={formData.severity}
                                                onValueChange={(value) => setFormData({ ...formData, severity: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select severity" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="critical">Critical (Immediate danger to funds)</SelectItem>
                                                    <SelectItem value="high">High (Significant risk to users)</SelectItem>
                                                    <SelectItem value="medium">Medium (Potential exploit path)</SelectItem>
                                                    <SelectItem value="low">Low (Minor security issue)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Description & Impact</label>
                                            <Textarea
                                                placeholder="Detail the vulnerability and what it could allow an attacker to do."
                                                className="min-h-[120px]"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">Steps to Reproduce / PoC</label>
                                            <Textarea
                                                placeholder="Provide technical details or code to reproduce the issue."
                                                className="min-h-[150px] font-mono text-xs"
                                                value={formData.steps}
                                                onChange={(e) => setFormData({ ...formData, steps: e.target.value })}
                                                required
                                            />
                                        </div>

                                        <div className="pt-4">
                                            <Button
                                                type="submit"
                                                className="w-full gap-2 h-12 text-base font-bold shadow-lg shadow-primary/20"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <RefreshCcw className="h-5 w-5 animate-spin" />
                                                ) : (
                                                    <Send className="h-5 w-5" />
                                                )}
                                                {isSubmitting ? "Encrypting & Sending..." : "Submit Disclosure Securely"}
                                            </Button>
                                            <p className="text-[10px] text-center text-muted-foreground mt-4">
                                                By submitting, you agree to our Responsible Disclosure Policy and terms of service.
                                            </p>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>

            {/* SEO explainer — below the content, above footer */}
            <section className="container max-w-3xl mx-auto pb-16">
                <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
                    <h2 className="text-base font-semibold text-foreground">Responsible Disclosure</h2>
                    <p>
                        We take every security report seriously. If you have found a vulnerability in our smart
                        contracts, website, or API, please use the form above to report it. Do not share the
                        details publicly until we have fixed the issue.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Smart contract bugs</strong> — issues in presale, token factory, or locker contracts.</li>
                        <li><strong>Website vulnerabilities</strong> — XSS, injection, or access control problems.</li>
                        <li><strong>API security</strong> — authentication bypasses, data leaks, or rate-limit issues.</li>
                    </ul>
                    <p>
                        Our security team will confirm receipt within 24 hours and keep you updated on the fix.
                        We appreciate the efforts of security researchers who help us keep the platform safe for
                        all users.
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    )
}
